import { type RefObject, useState } from 'react';

import { useIntersectionObserver } from '@packages/shared/src/hooks/useIntersectionObserver/useIntersectionObserver';
import { useIsBot } from '@packages/tracking/src/hooks/useIsBot/useIsBot';

/*
 * This hook is used to defer rendering of a component until it is within a certain margin from the viewport.
 * It uses the Intersection Observer API to observe the component's position in the viewport.
 *
 * If `alwaysRender` is true, the component will always render regardless of its position in the viewport, and no observer will be created.
 * The `alwaysRender` flag could also be handled outside, but due to the rules of hooks (no conditional hooks), it is easier to handle it here.
 */
export const useDeferredRendering = ({
  ref,
  alwaysRender,
}: {
  // The ref of the component to observe.
  ref: RefObject<HTMLElement | null>;
  // Whether to always render the component and completely skip the observer.
  alwaysRender?: boolean;
}) => {
  // This is bascically a latch-type mechanism to prevent the component from rendering until it is in the viewport
  // It starts out as false, and can only be set to true by the observer
  // It never goes back to false right now, even if in theory the observer could also be used to "unmount" the component when it goes out of the viewport
  const [isCloseToViewport, setIsCloseToViewport] = useState(alwaysRender);
  const isBot = useIsBot();

  useIntersectionObserver(ref, {
    intersectOnce: true,
    threshold: 0,
    rootMargin: '500px 0px 500px 0px',
    callback: (isIntersecting) => {
      setIsCloseToViewport(isIntersecting);
    },
    // If alwaysRender is true, isCloseToViewport starts out as true anyways, so no observer is needed
    skip: alwaysRender,
  });

  return { showFullContents: alwaysRender || isBot || isCloseToViewport };
};
